/**
 * Invitation Zod schemas
 *
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 * All API responses must be parsed and validated with Zod schemas.
 * TypeScript types must be inferred from Zod schemas.
 */

import { z } from 'zod';

/**
 * Invitation scope enum
 */
export const InvitationScopeSchema = z.enum(['COMPANY', 'PROJECT']);

/**
 * Invitation status enum (derived)
 */
export const InvitationStatusSchema = z.enum(['PENDING', 'EXPIRED', 'REVOKED', 'ACCEPTED']);

/**
 * User invitation item schema
 *
 * Based on actual API response structure from /invitations/me
 */
export const UserInvitationItemSchema = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  role: z.string(),
  scope: z.string(),
  company_id: z.number().nullable().optional(),
  company_name: z.string().nullable().optional(),
  project_id: z.number().nullable().optional(),
  project_name: z.string().nullable().optional(),
  status: InvitationStatusSchema,
  created_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  revoked_at: z.string().nullable().optional(),
});

/**
 * TypeScript type inferred from Zod schema
 */
export type UserInvitationItem = z.infer<typeof UserInvitationItemSchema>;

/**
 * User invitations response schema
 */
export const UserInvitationsResponseSchema = z.object({
  invitations: z.array(UserInvitationItemSchema),
  pending_count: z.number().int().nonnegative(),
});

/**
 * TypeScript type for invitations response
 */
export type UserInvitationsResponse = z.infer<typeof UserInvitationsResponseSchema>;

/**
 * Reject invitation response schema
 */
export const RejectInvitationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type RejectInvitationResponse = z.infer<typeof RejectInvitationResponseSchema>;

/**
 * Accept invitation response schema
 */
export const AcceptInvitationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  company_id: z.number().nullable().optional(),
  project_id: z.number().nullable().optional(),
  role: z.string(),
});

export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponseSchema>;
